/**
 * Product pictures for the public site, drawn in HTML with the app's own tokens rather than screenshots, so they
 * stay sharp, stay in step with the app's look, and never show a real customer's numbers. The data is a sample company.
 */
import type { HTMLAttributes, ReactNode } from 'react';
import { Icons } from '../components/icons';
import { cx } from '../components/ui';

/** App-scale type inside the site's display-sized pages. */
const AppFrame = ({ className, ...rest }: HTMLAttributes<HTMLDivElement>) => <div {...rest} className={cx('font-sans text-[13px] leading-[1.45] text-ink [font-stretch:100%]', className)} />;

const Amount = ({ children, className }: { children: ReactNode; className?: string }) => <span className={cx('num font-medium', className)}>{children}</span>;

// --- Hero: the command center ---------------------------------------------------------------------------------------

const SIDEBAR: [ReactNode, string, number?][] = [
  [<Icons.Home />, 'Command center'],
  [<Icons.Inbox />, 'Approvals', 3],
  [<Icons.Sparkle />, 'Assistant'],
  [<Icons.Book />, 'Journal entries'],
  [<Icons.CheckCircle />, 'Close'],
  [<Icons.Users />, 'Customers'],
  [<Icons.Bill />, 'Bills'],
  [<Icons.Bank />, 'Cash'],
  [<Icons.Chart />, 'Reports'],
  [<Icons.Shield />, 'Controls'],
];

const KPIS = [
  { label: 'Cash', value: '$4,826,410', note: 'Across 3 accounts' },
  { label: 'ARR', value: '$6.12M', note: '+4.1% this month', good: true },
  { label: 'Net burn', value: '$212K', note: 'October to date' },
  { label: 'Runway', value: '31 months', note: 'At the 3-month average' },
];

const ATTENTION = [
  { tone: 'bg-ai-soft text-ai', icon: <Icons.Sparkle />, title: 'Accrual proposed: AWS, October usage', meta: 'Assistant · no bill yet, based on 6 months', amount: '$18,400.00', action: 'Review' },
  { tone: 'bg-info-soft text-info', icon: <Icons.Bank />, title: 'ACH credit matched to INV-1042', meta: 'Score 92 · exact amount, reference found', amount: '$24,000.00', action: 'Confirm' },
  { tone: 'bg-warn-soft text-warn', icon: <Icons.Inbox />, title: 'JE-2291: reclass to prepaid', meta: 'Waiting for a controller', amount: '$6,500.00', action: 'Approve' },
  { tone: 'bg-bad-soft text-bad', icon: <Icons.Clock />, title: 'Brightline Health: INV-1031 overdue', meta: '12 days past due', amount: '$9,600.00', action: 'Remind' },
];

const CONTROLS = ['Trial balance balances', 'Ledger hash chain intact', 'Bank accounts reconciled', 'No postings into closed periods', 'Suspense account is clear'];

export function CommandCenterMock() {
  return (
    <figure className="m-0">
      <figcaption className="sr-only">The RareMargin command center for a sample company: cash, ARR, burn and runway, items that need attention, and controls that are passing.</figcaption>
      <AppFrame className="flex overflow-hidden rounded-[14px] border border-line bg-paper text-left select-none" aria-hidden>
        <div className="hidden w-[196px] shrink-0 flex-col gap-0.5 bg-sidebar p-3 text-[12.5px] text-[#cfe2d8] md:flex">
          <div className="mb-3 flex items-center gap-2 px-2 pt-1 text-[13.5px] font-semibold text-white">
            <Icons.Logo size={20} /> Acme Cloud
          </div>
          {SIDEBAR.map(([icon, label, count], i) => (
            <div key={label} className={cx('flex items-center gap-2.5 rounded-md px-2 py-[7px]', i === 0 ? 'bg-sidebar-2 text-white' : 'opacity-80')}>
              <span className="opacity-80">{icon}</span>
              {label}
              {count && <span className="ml-auto rounded-full bg-[#2c5a47] px-1.5 text-[11px] text-white">{count}</span>}
            </div>
          ))}
        </div>
        <div className="min-w-0 flex-1 p-4 sm:p-5">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <div className="text-[17px] font-semibold tracking-[-0.01em]">Command center</div>
            <span className="rounded-full bg-brand-soft px-2 py-0.5 text-[11.5px] font-medium text-brand">October 2026 · day 3 of close</span>
            <span className="ml-auto hidden items-center gap-1.5 text-[12px] text-good sm:flex">
              <Icons.Chain /> Chain verified 2 min ago
            </span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2.5 lg:grid-cols-4">
            {KPIS.map((k) => (
              <div key={k.label} className="rounded-lg border border-line bg-surface p-3 shadow-card">
                <div className="text-[11.5px] text-muted">{k.label}</div>
                <Amount className="mt-1 block text-[18px] tracking-[-0.01em]">{k.value}</Amount>
                <div className={cx('mt-0.5 text-[11.5px]', k.good ? 'text-good' : 'text-faint')}>{k.note}</div>
              </div>
            ))}
          </div>
          <div className="mt-2.5 grid gap-2.5 lg:grid-cols-[minmax(0,1fr)_260px]">
            <div className="rounded-lg border border-line bg-surface shadow-card">
              <div className="flex items-center justify-between border-b border-line px-3.5 py-2.5">
                <span className="font-semibold">Needs attention</span>
                <span className="text-[11.5px] text-muted">4 items · 1 high priority</span>
              </div>
              {ATTENTION.map((a, i) => (
                <div key={a.title} className={cx('flex items-center gap-3 px-3.5 py-2.5', i > 0 && 'border-t border-line')}>
                  <span className={cx('grid size-7 shrink-0 place-items-center rounded-md', a.tone)}>{a.icon}</span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{a.title}</div>
                    <div className="truncate text-[11.5px] text-muted">{a.meta}</div>
                  </div>
                  <Amount className="hidden sm:block">{a.amount}</Amount>
                  <span className="hidden rounded-md border border-line-strong px-2 py-1 text-[11.5px] font-medium xl:block">{a.action}</span>
                </div>
              ))}
            </div>
            <div className="hidden rounded-lg border border-line bg-surface shadow-card lg:block">
              <div className="flex items-center justify-between border-b border-line px-3.5 py-2.5">
                <span className="font-semibold">Controls</span>
                <span className="text-[11.5px] text-good">All passing</span>
              </div>
              {CONTROLS.map((c) => (
                <div key={c} className="flex items-center gap-2 border-t border-line px-3.5 py-[9px] first:border-t-0">
                  <span className="text-good">
                    <Icons.CheckCircle size={15} />
                  </span>
                  <span className="truncate text-[12.5px]">{c}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </AppFrame>
    </figure>
  );
}

// --- Shared ---------------------------------------------------------------------------------------------------------

function Lines({ rows }: { rows: [string, string, string][] }) {
  return (
    <table className="mt-2 w-full text-[12.5px]">
      <thead>
        <tr className="text-left text-[11px] text-muted">
          <th className="py-1 font-medium">Account</th>
          <th className="py-1 text-right font-medium">Debit</th>
          <th className="py-1 text-right font-medium">Credit</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([a, dr, cr]) => (
          <tr key={a} className="border-t border-line">
            <td className="py-1.5">{a}</td>
            <td className="num py-1.5 text-right">{dr}</td>
            <td className="num py-1.5 text-right">{cr}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// --- Assistant ------------------------------------------------------------------------------------------------------

export const ASSISTANT_TABS = [
  { key: 'ask', title: 'Ask the ledger', body: 'Questions are answered from posted entries, with the accounts and amounts behind every figure.' },
  { key: 'draft', title: 'Draft the entry', body: 'Accruals, reclasses and corrections arrive as proposals. Nothing posts on its own.' },
  { key: 'approve', title: 'Approve with context', body: 'Each proposal shows who or what made it, why, and the exact lines it would post. Nobody approves their own work.' },
  { key: 'trace', title: 'Trace every change', body: 'Every record carries its source (app, API, MCP, AI, import or system) and every posting is sealed into the hash chain.' },
] as const;
export type AssistantTab = (typeof ASSISTANT_TABS)[number]['key'];

/** The picture beside the assistant tabs, on the dragonfruit panel. */
export function AssistantMock({ tab }: { tab: AssistantTab }) {
  return (
    <AppFrame aria-hidden className="select-none">
      <div className="rounded-[22px] bg-site-white/60 p-2 shadow-[0_30px_60px_-36px_rgb(70_2_47/0.4)] ring-1 ring-site-assist-2 sm:p-2.5">
        <div key={tab} className="min-h-[392px] animate-[site-fade_220ms_ease-out] rounded-xl bg-surface p-4 sm:p-5">
          {tab === 'ask' && <AskPanel />}
          {tab === 'draft' && <DraftPanel />}
          {tab === 'approve' && <ApprovePanel />}
          {tab === 'trace' && <TracePanel />}
        </div>
      </div>
    </AppFrame>
  );
}

function AskPanel() {
  return (
    <>
      <div className="ml-auto w-fit max-w-[85%] rounded-2xl rounded-br-md bg-brand px-3.5 py-2 text-white">Why is cloud hosting up in October?</div>
      <div className="mt-4 flex gap-2.5">
        <span className="grid size-7 shrink-0 place-items-center rounded-full bg-ai-soft text-ai">
          <Icons.Sparkle />
        </span>
        <div className="min-w-0 flex-1 text-[13px] leading-relaxed">
          <p>
            6100 Cloud hosting is <b className="num">$18,400</b> for October, up <b className="num">$2,950</b> (19%) on September. Nearly all of it is the AWS accrual: compute grew after the
            data-warehouse move on Oct 9.
          </p>
          <table className="mt-3 w-full text-[12.5px]">
            <thead>
              <tr className="text-left text-[11px] text-muted">
                <th className="py-1 font-medium">Vendor</th>
                <th className="py-1 text-right font-medium">Sep</th>
                <th className="py-1 text-right font-medium">Oct</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['AWS', '14,900.00', '17,820.00'],
                ['Cloudflare', '550.00', '580.00'],
              ].map(([v, a, b]) => (
                <tr key={v} className="border-t border-line">
                  <td className="py-1.5">{v}</td>
                  <td className="num py-1.5 text-right">{a}</td>
                  <td className="num py-1.5 text-right">{b}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 flex flex-wrap gap-1.5 text-[11.5px]">
            {['6100 Cloud hosting', 'JE-2297', 'AWS bills, 6 months'].map((s) => (
              <span key={s} className="rounded-md border border-line bg-paper px-1.5 py-0.5 text-muted">
                {s}
              </span>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function DraftPanel() {
  return (
    <>
      <div className="ml-auto w-fit max-w-[85%] rounded-2xl rounded-br-md bg-brand px-3.5 py-2 text-white">The Datadog annual plan went to expense. Fix it?</div>
      <div className="mt-4 rounded-xl border border-ai/25 bg-ai-soft/50 p-3.5">
        <div className="flex items-center justify-between">
          <span className="font-semibold">Draft · reclass to prepaid</span>
          <span className="rounded-full bg-warn-soft px-2 py-0.5 text-[11.5px] font-medium text-warn">Waiting for approval</span>
        </div>
        <p className="mt-1.5 text-[12.5px] text-ink-2">
          Bill B-0877 ($39,000.00, 12 months from Jan 1) was expensed in full. Moving the unused 2 months to prepaid, and amortizing $3,250.00 a month.
        </p>
        <Lines rows={[['1400 Prepaid expenses', '6,500.00', ''], ['6150 Software', '', '6,500.00']]} />
      </div>
      <div className="mt-3 text-[11.5px] text-muted">Proposals from the assistant use the same approval rules as people.</div>
    </>
  );
}

function ApprovePanel() {
  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11.5px] text-muted">Approval · JE-2291</div>
          <div className="text-[15px] font-semibold">Reclass to prepaid expenses</div>
        </div>
        <Amount className="text-[16px]">$6,500.00</Amount>
      </div>
      <dl className="mt-4 grid grid-cols-[110px_1fr] gap-y-2 border-y border-line py-3 text-[12.5px]">
        <dt className="text-muted">Prepared by</dt>
        <dd>Assistant, asked by Sam Ortiz</dd>
        <dt className="text-muted">Source</dt>
        <dd>
          <span className="rounded bg-sunken px-1.5 font-mono text-[11px]">AI</span>
        </dd>
        <dt className="text-muted">Policy</dt>
        <dd>Over $5,000: a controller approves</dd>
        <dt className="text-muted">Evidence</dt>
        <dd>Bill B-0877 · Datadog order form</dd>
      </dl>
      <Lines rows={[['1400 Prepaid expenses', '6,500.00', ''], ['6150 Software', '', '6,500.00']]} />
      <div className="mt-4 flex items-center gap-2">
        <span className="rounded-md bg-brand px-3 py-1.5 text-[12.5px] font-medium text-white">Approve and post</span>
        <span className="rounded-md border border-line-strong px-3 py-1.5 text-[12.5px] font-medium">Reject</span>
        <span className="ml-auto text-[11.5px] text-muted">Priya Shah, Controller</span>
      </div>
    </>
  );
}

function TracePanel() {
  const rows = [
    ['JE-2291 posted', 'AI → approved by Priya Shah', 'AI', 'c41e…9a07'],
    ['INV-1042 created', 'Billing sync API key', 'API', '7b2d…e115'],
    ['Payment matched', 'Sam Ortiz', 'UI', '0f9a…41cc'],
    ['October revenue recognized', 'Scheduler', 'SYSTEM', 'e83b…7d20'],
    ['Accrual proposed', 'Claude Code via MCP', 'MCP', 'pending'],
  ];
  return (
    <>
      <div className="flex items-center justify-between">
        <span className="font-semibold">Activity</span>
        <span className="flex items-center gap-1.5 text-[11.5px] text-good">
          <Icons.Chain /> 2,418 entries · chain intact
        </span>
      </div>
      <div className="mt-3">
        {rows.map(([what, who, src, hash]) => (
          <div key={what} className="grid grid-cols-[1fr_auto] items-center gap-x-3 border-t border-line py-2.5 first:border-t-0 sm:grid-cols-[1fr_70px_96px]">
            <div className="min-w-0">
              <div className="truncate font-medium">{what}</div>
              <div className="truncate text-[11.5px] text-muted">{who}</div>
            </div>
            <span className="w-fit rounded bg-sunken px-1.5 font-mono text-[11px]">{src}</span>
            <span className="hidden text-right font-mono text-[11px] text-faint sm:block">{hash}</span>
          </div>
        ))}
      </div>
    </>
  );
}
